/**
 * @classdesc
 * A collection of values based on {geodata.CharacteristicCollection}.
 * Values are keyed by their data referece key, which are grouped by the
 * feature reference or feature record key.
 *
 * @property {ValueSources} sources - IDs from FeatureCollection & CharacteristicCollection
 */
class ValueCollection {
    /**
     * @param {object} props
     */
    constructor(props, sources = {}) {
        Object.assign(this, props);
        // Optionally pass along source identifiers for later consumption
        Object.defineProperty(this, 'sources', { enumerable: false, value: sources });
        // Sealing this to allow memoizing the results of lookups for efficiency
        Object.seal(this);
    }
    /**
     * There is data for all feature / data reference combinations.
     * @param {...(feature-references.FeatureReference|feature-references.FeatureRecord)} [features]
     * @return {boolean}
     */
    hasAllValues(...features) {
        const featureKeys = featureKeysForArguments(this, features);
        const hasDataByFeature = featureKeys.map(checkFor(this, hasEveryValue));
        return hasDataByFeature.every(identity);
    }
    /**
     * There is data for some feature / data reference combinations, maybe all.
     * @param {...(feature-references.FeatureReference|feature-references.FeatureRecord)} [features]
     * @return {boolean}
     */
    hasSomeValues(...features) {
        const featureKeys = featureKeysForArguments(this, features);
        const hasDataByFeature = featureKeys.map(checkFor(this, hasSomeValues));
        return hasDataByFeature.some(identity);
    }
    /**
     * There is no data for any feature / data reference combination.
     * @param {...(feature-references.FeatureReference|feature-references.FeatureRecord)} [features]
     * @return {boolean}
     */
    hasNoValues(...features) {
        const featureKeys = featureKeysForArguments(this, features);
        const hasDataByFeature = featureKeys.map(checkFor(this, hasNoValues));
        return hasDataByFeature.every(identity);
    }
    /**
     * @param {feature-references.FeatureReference|feature-references.FeatureRecord} feature
     * @param {data-references.DataReference|data-references.DataRecord} data
     * @return {*}
     */
    valueFor(feature, data) {
        const scoped = this[feature.key] || {};
        return scoped[data.key];
    }
    /**
     * Retrieve a dictionary of values for provided feature, with property names
     * as {data-references.DataReference} keys.
     * @param {feature-references.FeatureReference|feature-references.FeatureRecord} feature
     * @return {ScopedValueCollection}
     */
    valuesForFeature(feature) {
        return this[feature.key];
    }
    /**
     * Retrieve a dictionary of values for data reference, keyed by individual
     * {FeatureReference}s.
     * @param {data-references.DataReference} data
     * @return {ScopedValueCollection}
     */
    valuesForData(data) {
        const features = allFeatureKeys(this);
        return features.reduce((memo, featureKey) => {
            memo[featureKey] = this[featureKey][data.key];
            return memo;
        }, {});
    }
    /**
     * Reformat the data for easy value retrieval as well as define optional
     * normalized results.
     * @param {geodata.FeatureCollection} featureCollection
     * @param {geodata.CharacteristicCollection} characteristicCollection
     * @param {object} [opts]
     * @param {data-references.DataReferenceCollection[][]} [opts.normalize] List of DataReferenceCollection pairs
     * @return {ValueCollection}
     */
    static from(featureCollection, characteristicCollection, opts = {}) {
        const byFeature = characteristicCollection.features.reduce((memo, feature) => {
            memo[feature.id] = feature;
            return memo;
        }, {});
        // Define value for each DataReference included; build from superset
        const valuesByFeature = featureCollection.features.reduce((memo, feature) => {
            const source = byFeature[feature.id];
            const data = source ? source.properties : {};
            memo[feature.id] = data;
            return memo;
        }, {});
        // When normalization details are provided, generate normalized values too
        if (opts.normalize && opts.normalize.length) {
            normalize(valuesByFeature, ...opts.normalize);
        }
        return new ValueCollection(valuesByFeature, {
            featureCollection: featureCollection.id,
            characteristicCollection: characteristicCollection.id,
        });
    }
}
/**
 * @private
 * @param {ValueCollection} context
 * @param {function} checkFn - Operation to pass subcontext
 * @return {function}
 */
function checkFor(context, checkFn) {
    return featureKey => checkFn(context[featureKey]);
}
/**
 * @private
 * @param {object} byDataReference
 * @return {boolean}
 */
function hasEveryValue(byDataReference) {
    return Object.values(byDataReference).every(value => value !== null && value !== undefined);
}
/**
 * @private
 * @param {object} byDataReference
 * @return {boolean}
 */
function hasSomeValues(byDataReference) {
    return Object.values(byDataReference).some(value => value !== null && value !== undefined);
}
/**
 * @private
 * @param {object} byDataReference
 * @return {boolean}
 */
function hasNoValues(byDataReference) {
    return Object.values(byDataReference).every(value => value === null || value === undefined);
}
/**
 * @private
 * @param {data-references.DataReferenceCollection} numeratorCollection
 * @param {data-references.DataReferenceCollection} denominatorCollection
 * @return {data-references.DataReference[]}
 */
function zip(numeratorCollection, denominatorCollection) {
    let lastDenominator = null;
    return numeratorCollection.variables.map((ref, index) => {
        // Use index specified denominator by default
        let denominator = denominatorCollection.variables[index];
        // If no index based on specified, use last specified
        if (denominator) {
            lastDenominator = denominator;
        }
        else {
            denominator = lastDenominator;
        }
        return [ref, denominator];
    });
}
/**
 * @private
 * @param {feature-references.FeatureReference[]|feature-references.FeatureRecord[]} features
 * @return {string[]}
 */
function featureKeys(features) {
    return features.map(feature => feature.key);
}
/**
 * @private
 * @param {ValueCollection} collection
 * @return {string[]}
 */
function allFeatureKeys(collection) {
    return Object.keys(collection);
}
/**
 * @private
 * @param {ValueCollection} collection
 * @param {feature-references.FeatureReference[]|feature-references.FeatureRecord[]} [features]
 * @return {string[]}
 */
function featureKeysForArguments(collection, features) {
    return features.length === 0 ? allFeatureKeys(collection) : featureKeys(features);
}
/**
 * @private
 * @param {*} value
 * @return {boolean}
 */
function isFiniteValue(value) {
    return (typeof value === 'number'
        && Number.isFinite(value));
}
/**
 * @private
 * @description Append normalized values based on provided pairs of
 * {DataReferenceCollection}s, where the first is the numerator, and the
 * second is the denominator.
 * @this {object} uninitialized {ValueCollection}
 * @param {object} valuesByFeature
 * @param {...data-references.DataReferenceCollection[]} pair
 */
function normalize(valuesByFeature, ...pairs) {
    const features = allFeatureKeys(valuesByFeature);
    pairs.forEach(([numeratorCollection, denominatorCollection]) => {
        const referencePairs = zip(numeratorCollection, denominatorCollection);
        features.forEach((featureKey) => {
            referencePairs.forEach(([numerator, denominator]) => {
                const normalizedKey = [numerator.key, denominator.key].join('/');
                const data = valuesByFeature[featureKey];
                const normalizedValue = data[numerator.key] / data[denominator.key];
                // The normalized value should always be a finite number or null
                const value = isFiniteValue(normalizedValue) ? normalizedValue : null;
                data[normalizedKey] = value;
            });
        });
    });
}
function identity(value) {
    return value;
}
export default ValueCollection;
